import { debounce } from '@theme/utilities';

// Order History & Track Order — tab filtering, search, and list/detail wiring.
// Presentational only: all data is server-rendered from real order/fulfillment
// Liquid objects (or the equivalent Kwikpass-JS markup); this just shows/hides
// what's already in the DOM.
class OrderHistoryApp extends HTMLElement {
  #controller = new AbortController();
  #activeGroup = 'all';
  #searchTerm = '';
  #handleSearchInput = debounce((event) => {
    this.#searchTerm = event.target.value || '';
    this.#applyFilters();
  }, 150);

  connectedCallback() {
    const { signal } = this.#controller;

    this.layout = this.querySelector('[data-order-layout]');
    this.list = this.querySelector('[data-order-list]');
    this.detail = this.querySelector('[data-order-detail]');
    this.searchInput = this.querySelector('[data-order-search-input]');
    this.emptyState = this.querySelector('[data-order-empty]');

    this.addEventListener('click', this.#handleClick, { signal });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.#handleSearchInput, { signal });
    }

    this.#restoreFromQueryParam();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#handleSearchInput.cancel();
  }

  #handleClick = (event) => {
    const filterButton = event.target.closest('[data-order-filter]');
    if (filterButton) {
      this.#setActiveGroup(filterButton);
      return;
    }

    const trackButton = event.target.closest('[data-track-order]');
    if (trackButton) {
      this.#showDetail(trackButton.dataset.orderId);
      return;
    }

    const backButton = event.target.closest('[data-order-back]');
    if (backButton) {
      this.#showList();
      return;
    }

    const copyButton = event.target.closest('[data-copy-awb]');
    if (copyButton) {
      this.#copyAwb(copyButton);
      return;
    }

    const detailsToggle = event.target.closest('[data-order-details-toggle]');
    if (detailsToggle) {
      this.#toggleOrderDetails(detailsToggle);
    }
  };

  #toggleOrderDetails(button) {
    const card = button.closest('[data-order-card]');
    const panel = card?.querySelector('[data-order-inline-details]');
    if (!panel) return;

    const isOpen = !panel.hidden;
    panel.hidden = isOpen;
    button.setAttribute('aria-expanded', String(!isOpen));

    const label = button.querySelector('[data-order-details-toggle-label]');
    if (label) label.textContent = isOpen ? 'Show details' : 'Hide details';
  }

  #setActiveGroup(filterButton) {
    this.#activeGroup = filterButton.dataset.orderFilter;

    this.querySelectorAll('[data-order-filter]').forEach((button) => {
      const isActive = button === filterButton;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    this.#applyFilters();
  }

  #applyFilters() {
    const term = this.#searchTerm.trim().toLowerCase();
    let visibleCount = 0;

    this.querySelectorAll('[data-order-card]').forEach((card) => {
      const matchesGroup = this.#activeGroup === 'all' || card.dataset.orderStatusGroup === this.#activeGroup;
      const matchesSearch = term === '' || (card.dataset.orderSearch || '').includes(term);
      const visible = matchesGroup && matchesSearch;
      card.hidden = !visible;
      if (visible) visibleCount += 1;
    });

    if (this.emptyState) this.emptyState.hidden = visibleCount !== 0;
  }

  // Lets /pages/track-order send someone straight to a specific order: it
  // signs them in via the real Kwikpass OTP flow, then returns here with
  // ?order=<number>. No fabricated lookup — just a search prefill once the
  // customer's real orders are on the page.
  #restoreFromQueryParam() {
    if (!this.searchInput) return;

    const params = new URLSearchParams(window.location.search);
    const orderParam = params.get('order');
    if (!orderParam) return;

    this.searchInput.value = orderParam;
    this.#searchTerm = orderParam;
    this.#applyFilters();

    params.delete('order');
    const query = params.toString();
    const url = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
    window.history.replaceState(null, '', url);
  }

  #showDetail(orderId) {
    if (!this.detail) return;

    this.querySelectorAll('[data-order-card]').forEach((card) => {
      card.classList.toggle('is-selected', card.dataset.orderId === orderId);
    });

    this.querySelectorAll('[data-order-detail-id]').forEach((panel) => {
      panel.hidden = panel.dataset.orderDetailId !== orderId;
    });

    this.detail.hidden = false;
    this.layout?.classList.add('is-viewing-detail');
    this.detail.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  #showList() {
    this.layout?.classList.remove('is-viewing-detail');
    this.list?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async #copyAwb(button) {
    const value = button.dataset.awbValue;
    if (!value || !navigator.clipboard) return;

    try {
      await navigator.clipboard.writeText(value);
      const originalLabel = button.textContent;
      button.textContent = 'Copied';
      setTimeout(() => {
        button.textContent = originalLabel;
      }, 2000);
    } catch (error) {
      // Clipboard access can be denied by the browser; nothing to recover here.
    }
  }
}

if (!customElements.get('order-history-app')) {
  customElements.define('order-history-app', OrderHistoryApp);
}
